/**
 * FAUCETS — kitchen faucet, bathroom sink faucet, tub faucet.
 *
 * Single source of the proportions shared by the 3D renderers
 * (`faucetRenderers.js`) and the 2D elevation schematics
 * (`KitchenFaucetSchematic.jsx`, `BathroomSinkFaucetSchematic.jsx`,
 * `BathroomTubFaucetSchematic.jsx`). Re-exported through `proceduralSpecs.js`;
 * kept in its own module because the three profiles together are as long as
 * the rest of that file.
 *
 * PURE — no Three.js, no React.
 *
 * Frame: every profile is expressed in the object's box. `u` runs along the
 * REACH axis (local +z, from the wall/back of the box into the room; the x
 * axis of a LEFT side elevation), `v` is UP measured from the BOTTOM of the
 * box (the deck for a deck faucet, the floor for a floor filler; the 3D
 * renderer subtracts `height/2`), `x` is across the width, box-centred.
 *
 * Spout centrelines are `faucetPath` paths (`{ start, segs }` in (u, v)) so
 * the renderer can sweep a tube along them and the schematic can offset the
 * same centreline into an outline.
 *
 * What the pipeline hands these renderers (so the box is filled correctly):
 *   - kitchen-faucet: `KITCHEN_FAUCET_DIMS` 4" x 14" x 11", base on the
 *     countertop (`buildKitchenFaucet`), the box's back edge 1" off the
 *     wall — so DEPTH carries the spout reach and the post sits at the back.
 *   - bathroom-sink-faucet: the catalog model's box, else 6" x 10" x 6"
 *     (`createSinkFaucet`), base on the counter / sink deck; wall-mount gets
 *     `wallMounted: true` and its centre 5" above the counter.
 *   - bathroom-tub-faucet: the catalog model's box, else 6" x 12" x 6"
 *     (`bathtubRules.FAUCET_DEFAULT_DIMS`); `faucetMountType` is
 *     'deck' | 'wall' | 'floor' (`placeTub.buildFaucet`).
 * Only the mount / style fields matter to the renderer; none of these objects
 * carries a body-style enum, so every scene shows the default variant.
 */

// Local copies of `IN_TO_M` / `clampSpec`: `proceduralSpecs.js` re-exports this
// module, so importing them from there would be a cycle.
const IN = 0.0254;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const deg = (d) => (d * Math.PI) / 180;

/**
 * Tube and trim stock, from the spec sheets of the products each profile is
 * drawn after (Moen Arbor 7594 / Kohler Simplice K-596 pull-down kitchen
 * faucets; Kohler Alteo K-45800 single-handle lavatory faucet; Kohler Purist
 * K-T14428 Roman tub filler). Meters.
 */
export const FAUCET_STOCK = {
  spoutR: 0.375 * IN, // 3/4" spout tube
  bodyR: 0.55 * IN, // 1.1" single-hole body
  flangeR: 1.1 * IN, // 2.2" escutcheon
  flangeH: 0.25 * IN,
  leverR: 0.22 * IN, // lever stock
  hubR: 0.45 * IN, // side-lever hub
  hubLen: 0.6 * IN,
  handleStemR: 0.42 * IN, // hot/cold handle stem
  handleFlangeR: 0.85 * IN,
  sprayR: 0.65 * IN, // pull-down spray head, 1.3" dia
  sprayLen: 2.5 * IN,
  plateT: 0.3 * IN, // wall / deck plate thickness
  centersetSpread: 2 * IN, // 4" centres -> +-2"
};

// ---------------------------------------------------------------------------
// Variant resolution — ONE resolver per type, used by both surfaces.
// ---------------------------------------------------------------------------

export const KITCHEN_FAUCET_VARIANTS = [
  'pull-down',
  'single-lever',
  'gooseneck',
  'two-handle',
  'wall-mount',
];

/**
 * Kitchen faucet body style. No pipeline field exists for it yet; accept the
 * style fields the elevation has always read. Default is the pull-down
 * high-arc — the single-hole pull-down is the standard kitchen faucet today.
 */
export function resolveKitchenFaucetVariant(obj = {}) {
  const raw = obj.subtype || obj.style || obj.spoutStyle || obj.mountType;
  if (raw === 'pulldown') return 'pull-down';
  if (raw === 'wallmount') return 'wall-mount';
  return KITCHEN_FAUCET_VARIANTS.includes(raw) ? raw : 'pull-down';
}

export const SINK_FAUCET_VARIANTS = [
  'single-handle',
  // A vessel filler: the same single-hole body and raked top lever, drawn
  // TALL so the spout clears a bowl standing on the counter. The catalog
  // tags 4 of its 22 lav faucets this way and the difference really is
  // height — 10.2" against 8.2" on average — which this profile already
  // takes as a parameter. Without the variant the mount fell through to
  // `single-handle`, so a vessel sink's placeholder drew a short deck
  // faucet: exactly the mismatch the picker now greys products for.
  'tall-single-handle',
  'centerset',
  'widespread',
  'wall-mount',
];

/**
 * Lavatory faucet drilling pattern — the catalog's `mountType` enum
 * (`FAUCET_MOUNT_TYPES` in glbConstants.js). `createSinkFaucet` stamps
 * `wallMounted: true` on a wall-mount and nothing else, so that flag is the
 * one field a real scene object carries. Default single-handle (single hole).
 */
export function resolveBathroomSinkFaucetVariant(obj = {}) {
  if (obj.wallMounted === true) return 'wall-mount';
  const raw = obj.mountType || obj.subtype || obj.style;
  if (raw === 'wallmount') return 'wall-mount';
  if (raw === 'single-lever') return 'single-handle';
  return SINK_FAUCET_VARIANTS.includes(raw) ? raw : 'single-handle';
}

export const TUB_FAUCET_VARIANTS = ['deck-mount', 'wall-mount', 'floor-mount'];

/**
 * Tub filler mount. `placeTub.buildFaucet` writes `faucetMountType` as
 * 'deck' | 'wall' | 'floor'; older objects carry `wallMounted` / `mountType`.
 */
export function resolveTubFaucetVariant(obj = {}) {
  if (obj.wallMounted === true) return 'wall-mount';
  const raw = obj.faucetMountType || obj.mountType || obj.subtype || obj.style;
  if (raw === 'wall' || raw === 'wallmount' || raw === 'wall-mount') {
    return 'wall-mount';
  }
  if (
    raw === 'floor' ||
    raw === 'floormount' ||
    raw === 'floor-mount' ||
    raw === 'freestanding'
  ) {
    return 'floor-mount';
  }
  return 'deck-mount';
}

// ---------------------------------------------------------------------------
// Centreline builders (u, v)
// ---------------------------------------------------------------------------

/**
 * High-arc "gooseneck": rise at `postU`, a full semicircle over to `tipU`, then
 * a straight drop of `drop`. Reach = tipU - postU. The apex sits at
 * `apexV` (the tube's TOP is apexV + r, so callers pass height - r).
 */
export function highArcPath({ postU, tipU, apexV, drop, riseFrom }) {
  const R = (tipU - postU) / 2;
  const cV = apexV - R;
  return {
    start: [postU, riseFrom],
    segs: [
      { kind: 'line', to: [postU, cV] },
      { kind: 'arc', c: [postU + R, cV], r: R, a0: Math.PI, a1: 0 },
      { kind: 'line', to: [tipU, cV - drop] },
    ],
    arcCentreV: cV,
    tipBottomV: cV - drop,
  };
}

/**
 * Low-arc lavatory / Roman spout: rise inside the body, a quarter bend of
 * `elbowR` onto a level run at `runV`, a smaller quarter bend down at the
 * tip and a short drop to the aerator.
 */
export function lowArcPath({ postU, tipU, runV, elbowR, tipElbowR, drop }) {
  const eR = Math.min(elbowR, (tipU - postU) * 0.7);
  const tR = Math.min(tipElbowR, tipU - postU - eR);
  return {
    start: [postU, runV - eR - 0.5 * IN],
    segs: [
      { kind: 'line', to: [postU, runV - eR] },
      {
        kind: 'arc',
        c: [postU + eR, runV - eR],
        r: eR,
        a0: Math.PI,
        a1: Math.PI / 2,
      },
      { kind: 'line', to: [tipU - tR, runV] },
      { kind: 'arc', c: [tipU - tR, runV - tR], r: tR, a0: Math.PI / 2, a1: 0 },
      { kind: 'line', to: [tipU, runV - tR - drop] },
    ],
    tipBottomV: runV - tR - drop,
  };
}

/** Level run off a wall plate at `plateU`, bending down `elbowR` to a drop. */
export function wallSpoutPath({ plateU, tipU, runV, elbowR, drop }) {
  const eR = Math.min(elbowR, (tipU - plateU) * 0.6);
  return {
    start: [plateU, runV],
    segs: [
      { kind: 'line', to: [tipU - eR, runV] },
      { kind: 'arc', c: [tipU - eR, runV - eR], r: eR, a0: Math.PI / 2, a1: 0 },
      { kind: 'line', to: [tipU, runV - eR - drop] },
    ],
    tipBottomV: runV - eR - drop,
  };
}

// ---------------------------------------------------------------------------
// Handle descriptors
// ---------------------------------------------------------------------------

/**
 * Side lever (kitchen single-hole): a hub on the +x side of the body and a
 * lever rising at `angle` in the x-v plane. Kept inside the box: the lever's
 * horizontal reach is whatever width remains beside the body.
 */
function sideLever({ width, bodyR, hubV, u }) {
  const s = FAUCET_STOCK;
  const room = Math.max(0.3 * IN, width / 2 - bodyR - s.hubLen);
  const angle = deg(60);
  const len = clamp(room / Math.cos(angle), 1 * IN, 3 * IN);
  return {
    kind: 'side',
    hub: { r: s.hubR, len: s.hubLen, x: bodyR + s.hubLen / 2, v: hubV, u },
    lever: {
      r: s.leverR,
      len,
      from: { x: bodyR + s.hubLen, v: hubV, u },
      dx: Math.cos(angle),
      dv: Math.sin(angle),
    },
  };
}

/**
 * Top lever (single-handle lavatory): rises from the body top, raked back
 * toward the wall at `angle` above horizontal, in the u-v plane.
 */
function topLever({ bodyR, bodyTopV, u, len, angle }) {
  const s = FAUCET_STOCK;
  return {
    kind: 'top',
    hub: { r: bodyR * 0.8, len: 0.4 * IN, x: 0, v: bodyTopV + 0.2 * IN, u },
    lever: {
      r: s.leverR,
      len,
      from: { x: 0, v: bodyTopV + 0.3 * IN, u },
      du: -Math.cos(angle),
      dv: Math.sin(angle),
    },
  };
}

/**
 * Hot/cold lever handle standing on the deck at (x, u): optional flange, a
 * stem, and a lever pointing FORWARD (+u) and up at `angle`.
 */
function deckHandle({ x, u, stemH, len, flange }) {
  const s = FAUCET_STOCK;
  const angle = deg(35);
  return {
    kind: 'deck',
    x,
    u,
    flange: flange ? { r: s.handleFlangeR, h: s.flangeH } : null,
    stem: { r: s.handleStemR, h: stemH },
    lever: {
      r: s.leverR,
      len,
      from: { x, v: stemH, u },
      du: Math.cos(angle),
      dv: Math.sin(angle),
    },
  };
}

/**
 * Wall-plate lever handle at (x, v): a stem out of the plate along +u and a
 * lever pointing forward and DOWN at 45 degrees.
 */
function wallHandle({ x, v, plateU, len }) {
  const s = FAUCET_STOCK;
  const stemLen = 1.2 * IN;
  const angle = deg(-45);
  return {
    kind: 'wall',
    x,
    v,
    stem: { r: s.handleStemR, len: stemLen, u: plateU },
    lever: {
      r: s.leverR,
      len,
      from: { x, v, u: plateU + stemLen },
      du: Math.cos(angle),
      dv: Math.sin(angle),
    },
  };
}

// ---------------------------------------------------------------------------
// Shared wall-mount profile (kitchen wall-mount, lavatory wall-mount, tub
// wall spout). The wall is the box's back face (u = -depth/2); the trim
// plate sits against it and fills the box height.
// ---------------------------------------------------------------------------
export function wallFaucetProfile({ width, height, depth }, { spoutR }) {
  const s = FAUCET_STOCK;
  const backU = -depth / 2;
  // The trim plate fills the box height. A short box (a wall spout with its
  // handles beside it) keeps the handles level with the spout; a tall one is
  // a wall-filler trim (Kohler Purist K-T14416): spout on top, handles below.
  const plateH = clamp(height * 0.9, 2.5 * IN, 9 * IN);
  const stacked = plateH >= 4.5 * IN;
  const plate = { w: width, h: plateH, t: s.plateT, v: height / 2, u: backU };
  const runV = stacked ? height * 0.72 : height * 0.55;
  const tipU = depth / 2 - spoutR;
  // Straight out, with only a slight down-turn at the tip (a wall tub spout
  // reaches 6"-8" level; the box depth is the reach).
  const elbowR = Math.min(1 * IN, spoutR * 1.6);
  const drop = clamp(runV - elbowR - spoutR, 0.3 * IN, 0.6 * IN);
  const spout = {
    r: spoutR,
    path: wallSpoutPath({ plateU: backU + plate.t, tipU, runV, elbowR, drop }),
  };
  const handleX = stacked
    ? Math.min(s.centersetSpread, width / 2 - s.handleStemR - 0.2 * IN)
    : width / 2 - s.handleStemR - 0.2 * IN;
  // Beside the spout, handles only when they clear it.
  const fits = stacked || handleX - s.handleStemR > spoutR + 0.4 * IN;
  const handles = fits
    ? [-1, 1].map((side) =>
        wallHandle({
          x: side * handleX,
          v: stacked ? height * 0.32 : runV,
          plateU: backU + plate.t,
          len: clamp(height * 0.3, 1.2 * IN, 2.5 * IN),
        })
      )
    : [];
  return { mount: 'wall', variant: 'wall-mount', plate, spout, handles };
}

// ---------------------------------------------------------------------------
// KITCHEN FAUCET
// ---------------------------------------------------------------------------

/**
 * Single-hole high-arc kitchen faucet (Moen Arbor: 15.5" tall, 9" reach,
 * 1.1" body, 2.2" escutcheon, side lever). The post stands at the BACK of
 * the box on the box's x centre — the pipeline centres the object over the
 * mounting hole — so only the lever reaches toward +width/2; the built width
 * is therefore narrower than the declared box on the -x side.
 */
export function kitchenFaucetProfile(dims, variant = 'pull-down') {
  const s = FAUCET_STOCK;
  const { width, height, depth } = dims;
  if (variant === 'wall-mount') {
    return wallFaucetProfile(dims, { spoutR: Math.min(s.spoutR, height / 8) });
  }
  const flangeR = Math.min(s.flangeR, width / 2, depth / 2);
  const bodyR = Math.min(s.bodyR, flangeR * 0.6);
  const spoutR = Math.min(s.spoutR, bodyR * 0.8);
  const pullDown = variant === 'pull-down';
  const tipR = pullDown ? Math.min(s.sprayR, bodyR) : spoutR;
  const postU = -depth / 2 + flangeR;
  const tipU = depth / 2 - tipR;
  const drop = clamp(height * 0.22, 1.5 * IN, 4 * IN);
  const path = highArcPath({
    postU,
    tipU,
    apexV: height - spoutR,
    drop,
    riseFrom: s.flangeH,
  });
  // Gooseneck: a slimmer, taller-reading spout — the body stops lower.
  const bodyTopV = clamp(
    height * (variant === 'gooseneck' ? 0.3 : 0.45),
    2 * IN,
    path.arcCentreV
  );
  const twoHandle = variant === 'two-handle';
  const profile = {
    mount: 'deck',
    variant,
    flange: { r: flangeR, h: s.flangeH, u: postU },
    body: { r: bodyR, u: postU, topV: bodyTopV },
    spout: { r: spoutR, path },
    sprayHead: pullDown
      ? {
          r: tipR,
          len: Math.min(s.sprayLen, drop * 0.8),
          u: tipU,
          bottomV: path.tipBottomV,
        }
      : null,
    lever: twoHandle
      ? null
      : sideLever({
          width,
          bodyR,
          hubV: clamp(height * 0.18, 2 * IN, bodyTopV - 0.5 * IN),
          u: postU,
        }),
    handles: twoHandle
      ? [-1, 1].map((side) =>
          deckHandle({
            x: side * (width / 2 - s.handleFlangeR),
            u: postU,
            stemH: clamp(height * 0.12, 1.2 * IN, 2 * IN),
            len: clamp(height * 0.14, 1.2 * IN, 2.2 * IN),
            flange: true,
          })
        )
      : [],
  };
  return profile;
}

// ---------------------------------------------------------------------------
// BATHROOM SINK FAUCET
// ---------------------------------------------------------------------------

/**
 * Lavatory faucet. Single-handle (Kohler Alteo: 6.9" tall, 5.1" reach, 1.4"
 * body, top lever raked back), 4" centerset on a deck plate, 8" widespread
 * (spout + two handles, each on its own flange), or wall-mount.
 */
export function bathroomSinkFaucetProfile(dims, variant = 'single-handle') {
  const s = FAUCET_STOCK;
  const { width, height, depth } = dims;
  if (variant === 'wall-mount') {
    return wallFaucetProfile(dims, { spoutR: Math.min(0.4 * IN, height / 8) });
  }
  const single =
    variant === 'single-handle' || variant === 'tall-single-handle';
  const flangeR = Math.min(s.flangeR, width / 2, depth / 2);
  const bodyR = Math.min(single ? 0.7 * IN : 0.55 * IN, flangeR * 0.65);
  const spoutR = Math.min(0.4 * IN, bodyR * 0.7);
  const postU = -depth / 2 + flangeR;
  const tipU = depth / 2 - spoutR;

  // Single-handle lever: a box wide enough for one gets a SIDE lever that
  // uses that width (Moen Gibson); a narrow box gets the raked TOP lever
  // (Kohler Alteo), which is then the tallest point, so the body stops
  // below it. Otherwise the spout apex fills the box.
  const sideLeverFits = width / 2 - bodyR - s.hubLen >= 1 * IN;
  const topLeverLen = clamp(height * 0.3, 1.5 * IN, 3 * IN);
  const topLeverAngle = deg(70);
  const topLevered = single && !sideLeverFits;
  // The spout tube's crest sits flush with the body top under a top lever;
  // otherwise the run crowns the body by one tube radius.
  const bodyTopV = topLevered
    ? height - topLeverLen * Math.sin(topLeverAngle) - s.leverR - 0.3 * IN
    : height - spoutR;
  const runV = topLevered ? bodyTopV - spoutR : bodyTopV;
  const path = lowArcPath({
    postU,
    tipU,
    runV,
    elbowR: 1.25 * IN,
    tipElbowR: 0.8 * IN,
    drop: 0.5 * IN,
  });

  const plate =
    variant === 'centerset'
      ? {
          w: Math.min(width, 6 * IN),
          h: 0.4 * IN,
          d: Math.min(2.4 * IN, depth * 0.5),
          u: -depth / 2 + Math.min(2.4 * IN, depth * 0.5) / 2,
        }
      : null;
  const handleX =
    variant === 'centerset'
      ? Math.min(s.centersetSpread, width / 2 - s.handleStemR - 0.2 * IN)
      : width / 2 - s.handleFlangeR;
  const handles = single
    ? []
    : [-1, 1].map((side) =>
        deckHandle({
          x: side * handleX,
          u: plate ? plate.u : postU,
          stemH: clamp(height * 0.14, 0.9 * IN, 1.6 * IN),
          len: clamp(height * 0.22, 1.2 * IN, 2.2 * IN),
          flange: variant === 'widespread',
        })
      );

  return {
    mount: 'deck',
    variant,
    plate,
    flange: plate ? null : { r: flangeR, h: s.flangeH, u: postU },
    body: { r: bodyR, u: postU, topV: bodyTopV },
    spout: { r: spoutR, path },
    lever: singleLever({
      single,
      topLevered,
      width,
      height,
      bodyR,
      bodyTopV,
      u: postU,
      len: topLeverLen,
      angle: topLeverAngle,
    }),
    handles,
  };
}

/** The one lever of a single-handle lavatory faucet, or none. */
function singleLever({
  single,
  topLevered,
  width,
  height,
  bodyR,
  bodyTopV,
  u,
  len,
  angle,
}) {
  if (!single) return null;
  if (topLevered) return topLever({ bodyR, bodyTopV, u, len, angle });
  return sideLever({
    width,
    bodyR,
    hubV: clamp(height * 0.4, 1.5 * IN, bodyTopV - 0.5 * IN),
    u,
  });
}

// ---------------------------------------------------------------------------
// BATHROOM TUB FAUCET
// ---------------------------------------------------------------------------

/**
 * Tub filler. Deck (Roman filler: spout between two handles, Kohler Purist
 * K-T14428, 8.5" reach), wall spout on a plate, or a floor-standing filler
 * column with a hand shower (Kohler Purist K-T97328: 36" tall).
 */
export function tubFaucetProfile(dims, variant = 'deck-mount') {
  const s = FAUCET_STOCK;
  const { width, height, depth } = dims;
  if (variant === 'wall-mount') {
    return wallFaucetProfile(dims, { spoutR: Math.min(0.75 * IN, height / 6) });
  }
  if (variant === 'floor-mount') return floorTubFillerProfile(dims);

  // Reach: a Roman filler reaches 8"-10" from its base. The spout uses the
  // WHOLE box depth — post flange at the back face, aerator at the front —
  // so reach = depth - flangeR - spoutR; the pipeline's 6"-deep fallback
  // box therefore caps it at ~4.6" (see the report / faucetSpecs header).
  const flangeR = Math.min(0.9 * IN, width / 4, depth / 2);
  const bodyR = Math.min(0.8 * IN, flangeR * 0.8);
  const spoutR = Math.min(0.55 * IN, bodyR * 0.7);
  const postU = -depth / 2 + flangeR;
  const tipU = depth / 2 - spoutR;
  const path = lowArcPath({
    postU,
    tipU,
    runV: height - spoutR,
    // One gentle bend over most of the reach, then a short level run.
    elbowR: (tipU - postU) * 0.6,
    tipElbowR: 0.75 * IN,
    drop: 0.5 * IN,
  });
  const handleX = Math.max(
    width / 2 - s.handleFlangeR,
    bodyR + s.handleFlangeR + 0.2 * IN
  );
  return {
    mount: 'deck',
    variant,
    plate: null,
    flange: { r: flangeR, h: s.flangeH, u: postU },
    body: { r: bodyR, u: postU, topV: height - spoutR },
    spout: { r: spoutR, path },
    lever: null,
    handles: [-1, 1].map((side) =>
      deckHandle({
        x: side * handleX,
        u: postU,
        // Roman-filler handles are ~3" (Purist K-T14428) beside an 8"-9" spout.
        stemH: clamp(height * 0.22, 1.5 * IN, 3 * IN),
        len: clamp(height * 0.25, 1.5 * IN, 3 * IN),
        flange: true,
      })
    ),
  };
}

function floorTubFillerProfile({ width, height, depth }) {
  const s = FAUCET_STOCK;
  // 3.5" base plate (Purist K-T97328); the spout arm reaches the rest of the
  // box depth (~8" over the rim on a 10"-deep box).
  const flangeR = Math.min(1.75 * IN, width / 2, depth / 2);
  const colR = Math.min(0.75 * IN, flangeR * 0.5);
  const spoutR = Math.min(0.55 * IN, colR * 0.75);
  const postU = -depth / 2 + flangeR;
  const tipU = depth / 2 - spoutR;
  const path = lowArcPath({
    postU,
    tipU,
    runV: height - spoutR,
    elbowR: (tipU - postU) * 0.6,
    tipElbowR: 0.75 * IN,
    drop: 0.5 * IN,
  });
  const showerR = Math.min(0.6 * IN, (width / 2 - colR) / 2.2);
  return {
    mount: 'floor',
    variant: 'floor-mount',
    plate: null,
    flange: { r: flangeR, h: 0.5 * IN, u: postU },
    body: { r: colR, u: postU, topV: height - spoutR },
    spout: { r: spoutR, path },
    // Hand shower hung in a cradle on the +x side of the column; the cradle
    // is the short bar tying it to the column.
    handShower: {
      r: showerR,
      len: clamp(height * 0.14, 3 * IN, 6 * IN),
      x: colR + showerR + 0.2 * IN,
      topV: height * 0.6,
      u: postU,
      cradleR: s.leverR,
    },
    // Single lever on the -x side, two thirds of the way up.
    lever: mirrorX(
      sideLever({ width, bodyR: colR, hubV: height * 0.7, u: postU })
    ),
    handles: [],
    stock: s,
  };
}

/** Flip a side-lever descriptor to the -x side. */
function mirrorX(lever) {
  return {
    ...lever,
    hub: { ...lever.hub, x: -lever.hub.x },
    lever: {
      ...lever.lever,
      from: { ...lever.lever.from, x: -lever.lever.from.x },
      dx: -lever.lever.dx,
    },
  };
}
